/**
 * CSV Formula Injection Security Utilities
 */

// Common CSV formula injection patterns
const CSV_INJECTION_PATTERNS = [
  /^=/, // Excel formulas start with =
  /^@/, // Some spreadsheet apps use @
  /^\+/, // Alternative formula prefix
  /^-[^0-9.]/, // Alternative formula prefix (minus sign)
  /^\t/, // Tab character can be used for injection
  /^\r/, // Carriage return
  /^\n/, // New line
];

/**
 * Validates a string against CSV formula injection patterns
 */
export const validateCSVSecurity = (value: string | number): boolean => {
  // Convert all values to string for validation (mirrors CSV export behavior)
  const stringValue = String(value);

  if (!stringValue || stringValue.length === 0) {
    return true; // Empty values are safe
  }

  const trimmedValue = stringValue.trim();
  if (trimmedValue.length === 0) {
    return true; // Empty strings are safe
  }
  // Check for basic injection patterns
  for (const pattern of CSV_INJECTION_PATTERNS) {
    if (pattern.test(trimmedValue)) {
      return false;
    }
  }
  return true;
};

/**
 * Sanitizes a value by prefixing dangerous characters with single quote
 */
export const sanitizeCSVValue = (value: string | number): string | number => {
  // Use validateCSVSecurity for all validation logic
  if (!validateCSVSecurity(value)) {
    // Prefix with single quote to neutralize formula
    return `'${String(value)}`;
  }
  return value;
};

/**
 * Sanitizes data array for CSV export
 */
export const sanitizeCSVData = (
  data: Record<string, string | number>[],
): {
  sanitizedData: Record<string, string | number>[];
  sanitizedCount: number;
} => {
  let sanitizedCount = 0;
  const sanitizedData = data.map((row) => {
    const sanitizedRow: Record<string, string | number> = {};
    Object.entries(row).forEach(([key, value]) => {
      const sanitizedValue = sanitizeCSVValue(value);
      if (sanitizedValue !== value) {
        sanitizedCount++;
      }
      sanitizedRow[key] = sanitizedValue;
    });
    return sanitizedRow;
  });
  return {
    sanitizedData,
    sanitizedCount,
  };
};
